import { useCallback, useEffect, useRef, useState } from 'react';
import { clearParticles } from './particles';
import { cutCelebrations } from './celebrations';
import { clearLayer } from './layer';
import { sound } from './sound';

/**
 * Honest-play rule 3: every celebration can be skipped with one tap.
 *
 *   const cel = useCelebration();
 *   cel.run(async (signal) => {
 *     await fx.countUp(winEl, 0, amount, 2600, { signal });   // jumps to total on skip
 *     await wait(300, signal);
 *     await fx.coinFly(winEl, wallet.target(), 12, { signal });
 *   });
 *
 * While running, the next pointerdown / Esc / Space / Enter anywhere aborts `signal`,
 * clears particles and flying coins; the click that follows the skip tap is swallowed
 * so it does not also press the button underneath (e.g. Spin).
 */
export interface Celebration {
  /** resolves to `true` when the celebration was skipped (then follow-ups like coins to the wallet are skipped too) */
  run(fn: (signal: AbortSignal) => Promise<void> | void): Promise<boolean>;
  skip(): void;
  active: boolean;
}

export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((res) => {
    if (signal?.aborted) return res();
    const t = setTimeout(res, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        res();
      },
      { once: true },
    );
  });
}

export function useCelebration(): Celebration {
  const ctrl = useRef<AbortController | null>(null);
  const [active, setActive] = useState(false);

  const skip = useCallback(() => {
    if (!ctrl.current) return;
    ctrl.current.abort();
    clearParticles();
    cutCelebrations();
    clearLayer();
    sound.cutCelebration();
  }, []);

  const run = useCallback(
    async (fn: (signal: AbortSignal) => Promise<void> | void): Promise<boolean> => {
      skip();
      const c = new AbortController();
      ctrl.current = c;
      setActive(true);

      const swallowClick = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      };
      const onPointer = () => {
        if (c.signal.aborted) return;
        skip();
        window.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }), 500);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          skip();
        }
      };
      window.addEventListener('pointerdown', onPointer, { capture: true });
      window.addEventListener('keydown', onKey, { capture: true });
      try {
        await fn(c.signal);
        return c.signal.aborted;
      } finally {
        window.removeEventListener('pointerdown', onPointer, { capture: true });
        window.removeEventListener('keydown', onKey, { capture: true });
        if (ctrl.current === c) {
          ctrl.current = null;
          setActive(false);
        }
      }
    },
    [skip],
  );

  useEffect(() => () => ctrl.current?.abort(), []);

  return { run, skip, active };
}
